import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Hammer, Sparkles, FileText, ArrowRight, Heart, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { resolveAssetUrl } from "@/lib/url";

const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
};
const truncate = (text, max = 160) => {
    const t = stripHtml(text).trim();
    if (t.length <= max) return t;
    return t.slice(0, max).trimEnd() + "…";
};

const CATEGORY_MAP = {
    aktualnosci: { label: "Aktualności", Icon: Sparkles, color: "bg-emerald-50 text-emerald-800" },
    wydarzenia: { label: "Wydarzenia", Icon: Calendar, color: "bg-amber-50 text-amber-800" },
    remonty: { label: "Remonty", Icon: Hammer, color: "bg-orange-50 text-orange-800" },
    organizacyjne: { label: "Organizacyjne", Icon: FileText, color: "bg-blue-50 text-blue-800" },
};

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("pl-PL", { day: "numeric", month: "short", year: "numeric" });
    } catch { return ""; }
};

export const Wiadomosci = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visible, setVisible] = useState(6);

    useEffect(() => {
        api.get("/news")
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    const featured = items[0];
    const rest = items.slice(1, visible);
    const remaining = Math.max(0, items.length - visible);

    return (
        <section id="wiadomosci" data-testid="wiadomosci-section" className="py-24 md:py-32 bg-[#FAF8F3]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl mb-16"
                >
                    <p className="text-[#D4A537] uppercase tracking-[0.25em] text-xs font-semibold mb-4">Życie osiedla</p>
                    <h2 className="font-serif text-4xl md:text-5xl text-[#092E22] leading-tight mb-4">
                        Wiadomości z osiedla
                    </h2>
                    <div className="divider-gold w-24 mb-6" />
                    <p className="text-gray-600 text-base md:text-lg">
                        Aktualności, wydarzenia, remonty i informacje organizacyjne – wszystko, co dzieje się w Dąbrówce Wiślanej.
                    </p>
                </motion.div>

                {loading && <p className="text-gray-500" data-testid="wiadomosci-loading">Wczytywanie...</p>}
                {!loading && items.length === 0 && (
                    <p className="text-gray-500" data-testid="wiadomosci-empty">Brak wiadomości do wyświetlenia.</p>
                )}

                {featured && (
                    <motion.article
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="grid md:grid-cols-2 gap-0 bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 mb-8"
                        data-testid={`news-featured-${featured.id}`}
                    >
                        <div className="relative aspect-[4/3] md:aspect-auto bg-[#0D4F3C]/10">
                            {featured.image_url && (
                                <img src={resolveAssetUrl(featured.image_url)} alt={featured.title} className="absolute inset-0 w-full h-full object-cover news-image-cap" />
                            )}
                        </div>
                        <div className="p-8 md:p-12 flex flex-col justify-center">
                            <div className="flex items-center gap-3 mb-4">
                                <NewsBadge category={featured.category} />
                                <span className="text-xs text-gray-500">{formatDate(featured.created_at)}</span>
                            </div>
                            <h3 className="font-serif text-3xl md:text-4xl text-[#092E22] mb-4 leading-tight">{featured.title}</h3>
                            <p className="text-gray-600 leading-relaxed mb-5">{truncate(featured.content, 240)}</p>
                            <Link
                                to={`/wiadomosc/${featured.slug || featured.id}`}
                                data-testid={`wiadomosc-featured-read-${featured.id}`}
                                className="inline-flex items-center gap-1.5 text-[#0D4F3C] hover:text-[#D4A537] text-sm font-medium"
                            >
                                Czytaj pełny artykuł <ArrowRight size={14} />
                            </Link>
                        </div>
                    </motion.article>
                )}

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((it, idx) => (
                        <motion.article
                            key={it.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: idx * 0.1 }}
                            data-testid={`news-card-${it.id}`}
                            className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                        >
                            <div className="relative aspect-[16/10] overflow-hidden bg-[#0D4F3C]/5">
                                {it.image_url && (
                                    <img
                                        src={resolveAssetUrl(it.image_url)}
                                        alt={it.title}
                                        className="w-full h-full object-cover news-image-cap group-hover:scale-105 transition-transform duration-700"
                                    />
                                )}
                            </div>
                            <div className="p-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <NewsBadge category={it.category} />
                                    <span className="text-xs text-gray-500">{formatDate(it.created_at)}</span>
                                </div>
                                <h3 className="font-serif text-xl text-[#092E22] mb-3 leading-snug">{it.title}</h3>
                                <p className="text-gray-600 text-sm leading-relaxed mb-3">{truncate(it.content, 130)}</p>
                                <div className="flex items-center justify-between gap-3">
                                    <Link
                                        to={`/wiadomosc/${it.slug || it.id}`}
                                        data-testid={`wiadomosc-read-more-${it.id}`}
                                        className="inline-flex items-center gap-1.5 text-[#0D4F3C] hover:text-[#D4A537] text-sm font-medium"
                                    >
                                        Czytaj więcej <ArrowRight size={14} />
                                    </Link>
                                    {((it.likes_count || 0) > 0 || (it.comments_count || 0) > 0) && (
                                        <div className="flex items-center gap-3 text-xs text-gray-500">
                                            {it.likes_count > 0 && (
                                                <span className="inline-flex items-center gap-1" data-testid={`news-likes-${it.id}`}>
                                                    <Heart size={12} className="text-[#D4A537]" /> {it.likes_count}
                                                </span>
                                            )}
                                            {it.comments_count > 0 && (
                                                <span className="inline-flex items-center gap-1" data-testid={`news-comments-${it.id}`}>
                                                    <MessageSquare size={12} className="text-[#0D4F3C]" /> {it.comments_count}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>

                {remaining > 0 && (
                    <div className="text-center mt-10">
                        <button
                            onClick={() => setVisible((v) => v + 6)}
                            data-testid="wiadomosci-load-more"
                            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white text-sm font-medium transition-all hover:-translate-y-0.5"
                        >
                            Pokaż więcej wiadomości ({remaining})
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

const NewsBadge = ({ category }) => {
    const meta = CATEGORY_MAP[category] || CATEGORY_MAP.aktualnosci;
    const Icon = meta.Icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${meta.color} text-xs font-semibold`}>
            <Icon size={11} /> {meta.label}
        </span>
    );
};

export default Wiadomosci;
